import type { ComponentProps, ReactNode } from 'react';
import { EmptyState, LastUpdate, Section, type Moment, type Waiting } from '@beyond-js/ui/react';
import { usePresence } from '@accounts/session';

/*
 * A heading with its description and actions, for the screens before the frame (sign-in, an
 * invitation), which stay centred. From `sm` up the actions sit beside the title, centred on its line,
 * with the description under both; on a phone they follow the description, in reading order.
 */
const titled = 'flex flex-col sm:grid sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:gap-x-3';
const below = 'sm:col-span-2';
const beside = 'mt-3 sm:col-start-2 sm:row-start-1 sm:mt-0';

/**
 * The heading of a screen before the frame. A signed-in screen's heading is the shared `PageHeader`,
 * through `Screen`.
 */
export function Heading({ title, lead, actions }: { title: string; lead?: string; actions?: ReactNode }) {
	return (
		<header className={titled}>
			<h1 tabIndex={-1} className="min-w-0 text-title-1 text-ink">{title}</h1>
			{lead ? <p className={`mt-1 max-w-reading text-body text-ink-muted ${below}`}>{lead}</p> : null}
			{actions ? <div className={`flex flex-wrap gap-2 ${beside}`}>{actions}</div> : null}
		</header>
	);
}

/**
 * The one bounded box of a screen before the frame, such as an invitation's answer. It keeps one inset on every
 * side (D184, `.card` in the theme): its first line's capitals, its last line's baseline or its controls' edges
 * and its words' start 16 px from its border, 20 px from 640 px.
 */
export function Card({ children }: { children: ReactNode }) {
	return <div className="card rounded-beyond border border-line bg-raised">{children}</div>;
}

/**
 * A region of a screen, as the family's flat `Section` (D52, LR-06): its heading, one description line
 * at the reading width, the actions at the end of the heading's line and its content, set apart from
 * the next by space and a divider rather than a bordered box. The section is the panel's outermost
 * element, so two panels in a row are two adjacent sections and the package draws the divider.
 *
 * `id` addresses the region through its heading: a link or an address (`/security#factors`,
 * `?view=members`) lands on the heading's words, which take the focus and, as a place reading starts,
 * draw nothing (D187).
 *
 * `waiting` (true or a shape) is the region's first read (LOAD-01): the heading, description and actions
 * are drawn at once and the body holds the shared wait, in the interface's language, until the content
 * replaces it.
 *
 * When Beyond Accounts does not answer the region's read (D163), the page's `Outage` says who did not
 * answer and offers Try again; the region says it only in its own place: `unavailable` puts "Not available
 * right now" in its body, and `updated` says over content kept from an earlier read when that was read
 * ("Last updated 12:04"), or "May be out of date" when that is not known.
 */
export function Panel({
	id,
	title,
	description,
	actions,
	waiting,
	unavailable,
	updated,
	children
}: {
	/** Makes the region addressable through its heading, so a deep link can bring someone to it and move focus there */
	id?: string;
	title: string;
	description?: string;
	actions?: ReactNode;
	waiting?: boolean | ComponentProps<typeof Waiting> | null;
	/** Its read did not answer and nothing of it is kept */
	unavailable?: boolean;
	/** When the content it keeps was read, while a later read does not answer; null when that is not known */
	updated?: Moment | null;
	children?: ReactNode;
}) {
	const { language } = usePresence();
	const shape = waiting ? { language, ...(waiting === true ? {} : waiting) } : null;
	const outage = unavailable ? { labels: EmptyState.labels[language] } : null;
	const kept = updated === undefined ? null : { time: updated, locale: language, labels: LastUpdate.labels[language] };
	const heading = id ? (
		<span id={id} tabIndex={-1} className="scroll-mt-4">
			{title}
		</span>
	) : (
		title
	);
	return (
		<Section title={heading} description={description} actions={actions} waiting={shape} unavailable={outage} updated={kept}>
			{children}
		</Section>
	);
}
